import { useCallback, useEffect, useState, lazy, Suspense } from "react";
import { X, ListChecks, ShieldAlert, ShieldCheck, LogOut, LogIn, Coffee, Info } from "lucide-react";
import { MapView, type CreateMode } from "@/components/MapView";
import { SearchBar } from "@/components/SearchBar";
import { SpotDetail } from "@/components/SpotDetail";
import { CreateSpot } from "@/components/CreateSpot";
import { AuthModal } from "@/components/AuthModal";
import {
  Disclaimer,
  isDisclaimerAcknowledged,
} from "@/components/Disclaimer";
import { AboutModal } from "@/components/AboutModal";
// Lazy-load heavy components that aren't needed on initial load
const MyContributions = lazy(() =>
  import("@/components/MyContributions").then((m) => ({ default: m.MyContributions })),
);
const AdminDashboard = lazy(() =>
  import("@/components/AdminDashboard").then((m) => ({ default: m.AdminDashboard })),
);
import { useAuth } from "@/hooks/useAuth";
import { useSpots, type BBox } from "@/hooks/useSpots";
import { useZones } from "@/hooks/useZones";
import { useLegality } from "@/hooks/useLegality";
import { getSpot, isCurrentUserAdmin } from "@/lib/api";
import type { Spot } from "@/lib/types";
import { t } from "@/lib/i18n";

/**
 * Root app component (spec 06 §2).
 *
 * Routing state machine (no router lib — single-page PWA):
 *   map → spot detail (bottom sheet) → create spot (bottom sheet)
 *        ↘ auth modal (sign-in gate)
 *        ↘ disclaimer (first-run gate / menu)
 *        ↘ my contributions (full-screen)
 *
 * Manages the Supabase client via the `useAuth` hook. Spot data flows from the
 * `useSpots` hook, which fetches on map idle.
 */

type Screen = "map" | "my-contributions" | "admin";

export default function App() {
  const auth = useAuth();
  const { spots, setBounds, refresh } = useSpots();
  const { zones, setBounds: setZonesBounds } = useZones();

  const [screen, setScreen] = useState<Screen>(() =>
    typeof window !== "undefined" && window.location.hash === "#admin"
      ? "admin"
      : "map",
  );
  const [selectedSpot, setSelectedSpot] = useState<Spot | null>(null);
  const [createMode, setCreateMode] = useState<CreateMode>("idle");
  const [pinCoord, setPinCoord] = useState<{ lng: number; lat: number } | null>(
    null,
  );
  const [showAuth, setShowAuth] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [disclaimerRequired, setDisclaimerRequired] = useState(false);
  const [userLocation, setUserLocation] = useState<{
    lng: number;
    lat: number;
  } | null>(null);
  const [flyToTarget, setFlyToTarget] = useState<{ lng: number; lat: number } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Live legality for the create-flow pin (debounced via useLegality).
  const legality = useLegality(
    createMode !== "idle" ? pinCoord?.lng ?? null : null,
    createMode !== "idle" ? pinCoord?.lat ?? null : null,
  );

  // Detect admin membership when the user changes (gates the menu entry / route).
  useEffect(() => {
    if (!auth.user) {
      setIsAdmin(false);
      return;
    }
    isCurrentUserAdmin()
      .then(setIsAdmin)
      .catch(() => setIsAdmin(false));
  }, [auth.user]);

  // Keep the location hash in sync with the admin screen (deep-linkable).
  useEffect(() => {
    const target = screen === "admin" ? "#admin" : "";
    if (window.location.hash !== target) {
      window.history.replaceState(null, "", window.location.pathname + target);
    }
  }, [screen]);

  // ---- First-run disclaimer gate (spec 06 §2 screen 5) ----
  useEffect(() => {
    if (!isDisclaimerAcknowledged()) {
      setShowDisclaimer(true);
      setDisclaimerRequired(true);
    }
  }, []);

  // ---- Best-effort geolocation (no stored GPS history — spec AGENTS §5.7) ----
  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) =>
        setUserLocation({
          lng: pos.coords.longitude,
          lat: pos.coords.latitude,
        }),
      () => {
        /* geolocation denied — map stays at default center */
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }, []);

  // ---- Handlers ----

  const handleBoundsChange = useCallback(
    (b: BBox) => {
      setBounds(b);
      setZonesBounds(b);
    },
    [setBounds, setZonesBounds],
  );

  const handleSpotSelect = useCallback(async (spot: Spot) => {
    // The map emits a partial spot from feature properties; fetch the full row.
    try {
      const full = await getSpot(spot.id);
      setSelectedSpot(full);
    } catch {
      // Fall back to the partial data if the fetch fails (offline).
      setSelectedSpot(spot);
    }
  }, []);

  const startCreate = useCallback(
    (lng: number, lat: number) => {
      // Gate on auth: spot creation requires an authenticated session (spec 05 §2).
      if (!auth.user) {
        setShowAuth(true);
        return;
      }
      setPinCoord({ lng, lat });
      setCreateMode("positioning");
      setSelectedSpot(null);
    },
    [auth.user],
  );

  const confirmPin = useCallback(() => {
    // Legality is a warning, not a hard block — always allow advancing.
    // The badge informs the user; they can proceed at their own risk.
    if (legality.loading) return;
    setCreateMode("details");
  }, [legality.loading]);

  const cancelCreate = useCallback(() => {
    setCreateMode("idle");
    setPinCoord(null);
  }, []);

  const handleCreated = useCallback(
    (spotId: string) => {
      setCreateMode("idle");
      setPinCoord(null);
      refresh();
      void getSpot(spotId).then(setSelectedSpot).catch(() => {});
    },
    [refresh],
  );

  // ---- Render ----

  return (
    <div className="fixed inset-0 flex flex-col bg-bg text-fg">
      {/* Map (always mounted; sheets overlay on top) */}
      <div className="relative flex-1">
        <MapView
          spots={spots}
          zones={zones}
          onBoundsChange={handleBoundsChange}
          onSpotSelect={handleSpotSelect}
          onCreateSpot={startCreate}
          userLocation={userLocation}
          createMode={createMode}
          pinCoord={pinCoord}
          onPinMove={(lng, lat) => setPinCoord({ lng, lat })}
          onConfirmPin={confirmPin}
          onCancelCreate={cancelCreate}
          legalityState={legality.state}
          legalityResult={legality.result}
          legalityLoading={legality.loading}
          flyToTarget={flyToTarget}
          onFlyComplete={() => setFlyToTarget(null)}
        />

        {/* Address search bar */}
        <SearchBar
          onSelect={(lng, lat) => setFlyToTarget({ lng, lat })}
        />
      </div>

      {/* Top-left logo + menu button */}
      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        className="absolute left-3 top-[calc(env(safe-area-inset-top,0px)+0.75rem)] z-10 flex items-center gap-1.5 rounded-full bg-surface/95 py-1 pl-1 pr-3 shadow-lg ring-1 ring-line transition active:scale-95"
        aria-label={t("nav.map")}
      >
        <img src="/logo-nav.webp" alt="Stnr.Space" className="h-8 w-8 shrink-0 rounded-full" />
        <span className="font-[Ranchers,sans-serif] text-base leading-none tracking-wide whitespace-nowrap">
          <span className="text-accent">STNR</span><span className="text-fg">.SPACE</span>
        </span>
      </button>

      {/* Slide-in menu */}
      {menuOpen ? (
        <div className="fixed inset-0 z-40 fade-in">
          <button
            type="button"
            aria-label={t("create.cancel")}
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-black/50"
          />
          <nav className="absolute left-0 top-0 h-full w-72 max-w-[80vw] bg-bg-elevated p-4 pt-[calc(env(safe-area-inset-top,0px)+1.25rem)] shadow-2xl sheet-up">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-lg font-bold">{t("app.name")}</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="rounded-full p-3 text-fg-muted hover:bg-surface"
                aria-label={t("create.cancel")}
              >
                <X size={20} />
              </button>
            </div>

            <ul className="space-y-1">
              <MenuButton
                icon={<ListChecks size={18} />}
                label={t("nav.myContributions")}
                disabled={!auth.user}
                onClick={() => {
                  setMenuOpen(false);
                  if (auth.user) setScreen("my-contributions");
                  else setShowAuth(true);
                }}
              />
              <MenuButton
                icon={<ShieldAlert size={18} />}
                label={t("nav.disclaimer")}
                onClick={() => {
                  setMenuOpen(false);
                  setDisclaimerRequired(false);
                  setShowDisclaimer(true);
                }}
              />
              <MenuButton
                icon={<Info size={18} />}
                label={t("nav.about")}
                onClick={() => {
                  setMenuOpen(false);
                  setShowAbout(true);
                }}
              />
              {isAdmin ? (
                <MenuButton
                  icon={<ShieldCheck size={18} />}
                  label={t("nav.admin")}
                  onClick={() => {
                    setMenuOpen(false);
                    setScreen("admin");
                  }}
                />
              ) : null}
              <li className="my-2 h-px bg-line" />
              {auth.user ? (
                <MenuButton
                  icon={<LogOut size={18} />}
                  label={t("nav.signOut")}
                  onClick={() => {
                    setMenuOpen(false);
                    void auth.signOut();
                  }}
                />
              ) : (
                <MenuButton
                  icon={<LogIn size={18} />}
                  label={t("nav.signIn")}
                  onClick={() => {
                    setMenuOpen(false);
                    setShowAuth(true);
                  }}
                />
              )}
            </ul>
          </nav>
        </div>
      ) : null}

      {/* Spot detail bottom sheet */}
      {selectedSpot ? (
        <SpotDetail
          spot={selectedSpot}
          user={auth.user}
          userLocation={userLocation}
          onClose={() => setSelectedSpot(null)}
          onRequireAuth={() => setShowAuth(true)}
        />
      ) : null}

      {/* Create spot bottom sheet (details phase — pin already positioned) */}
      {createMode === "details" && pinCoord ? (
        <CreateSpot
          lng={pinCoord.lng}
          lat={pinCoord.lat}
          onClose={cancelCreate}
          onCreated={handleCreated}
        />
      ) : null}

      {/* Auth modal */}
      {showAuth ? (
        <AuthModal auth={auth} onClose={() => setShowAuth(false)} />
      ) : null}

      {/* Disclaimer (first-run gate or menu-reopened) */}
      {showDisclaimer ? (
        <Disclaimer
          required={disclaimerRequired}
          onAccept={() => {
            setShowDisclaimer(false);
            setDisclaimerRequired(false);
          }}
        />
      ) : null}

      {/* My contributions full-screen */}
      {screen === "my-contributions" && auth.user ? (
        <Suspense fallback={null}>
          <MyContributions
            user={auth.user}
            onClose={() => setScreen("map")}
            onSpotSelect={(s) => {
              setScreen("map");
              setSelectedSpot(s);
            }}
          />
        </Suspense>
      ) : null}

      {/* Ko-fi support button */}
      <a
        href="https://ko-fi.com/I2D821LCGP"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-3 right-3 z-20 flex items-center gap-1.5 rounded-full bg-[#ff5e5b] px-3 py-1.5 text-xs font-semibold text-white shadow-lg ring-1 ring-white/10 transition hover:brightness-110 active:scale-95"
        aria-label="Support us on Ko-fi"
      >
        <Coffee size={14} />
        Ko-fi
      </a>

      {/* Admin moderation dashboard (deep-linkable via #admin).
          Gated by auth.user AND isAdmin — defense-in-depth on top of the
          AdminDashboard's own server-side isCurrentUserAdmin() check. */}
      {screen === "admin" && auth.user && isAdmin ? (
        <Suspense fallback={null}>
          <AdminDashboard onClose={() => setScreen("map")} />
        </Suspense>
      ) : null}

      {/* About modal */}
      {showAbout ? (
        <AboutModal onClose={() => setShowAbout(false)} />
      ) : null}
    </div>
  );
}

// ---- Small inline menu button ----

function MenuButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-fg hover:bg-surface disabled:opacity-40"
      >
        {icon}
        {label}
      </button>
    </li>
  );
}
